<template>
  <module-template
    id="scrolltainer"
    title
    :canUpload="vaultWriteAccess"
    canList
    :canDownload="canDownload"
    :isDownloading="downloading"
    canCompareFiles
    @list="listFiles"
    @download="onDownload"
    @upload="onShowUpload"
  >
    <template #title>
      <v-row dense class="px-1" align="center">
        {{ $t("global.concepts.file") }}
        <v-icon color="white" class="px-3" size="9">pic-arrow-right</v-icon>
        <div>{{ file?.name }}</div>
      </v-row>
    </template>

    <v-card>
      <v-card-text>
        <v-row dense style="max-width: 800px">
          <v-col style="font-weight: bold; text-align: left" cols="3">
            {{ $t("global.administration.vault") }}
          </v-col>

          <v-col cols="2">
            {{ file?.vaultName }}
          </v-col>
        </v-row>

        <v-row dense style="max-width: 800px">
          <v-col style="font-weight: bold; text-align: left" cols="3">
            {{ $t("global.concepts.file-environment") }}
          </v-col>

          <v-col cols="2">
            <v-chip small outlined v-if="file?.fileEnvIsLocal">LOCAL</v-chip>
            <file-env-chip
              v-if="!file?.fileEnvIsLocal"
              :color="file?.fileEnvColor"
              :label="file?.fileEnvLabel"
            >
            </file-env-chip>
          </v-col>
        </v-row>

        <v-row
          dense
          style="max-width: 800px"
          v-if="file?.fileEnvIsLocal && $store.state.bpInstReadPermission"
        >
          <v-col style="font-weight: bold; text-align: left" cols="3">
            {{ $t("global.business-process.bp-instance") }}
          </v-col>

          <v-col cols="2">
            <router-link
              :to="{
                name: 'BP instance',
                params: { bpId: file?.bpId },
              }"
              class="ooliba-font-color-blue"
            >
              {{ " #" + file?.bpId }}
            </router-link>
          </v-col>
        </v-row>

        <ooliba-basic-table
          v-model="selectedFileVersions"
          :headers="headers"
          :items="items"
          :loading="loading"
          item-key="versionNumber"
          no-pagination
          class="mt-2"
          @row-clicked="onRowClick"
        >
          <template #[`item.createdBy`]="{ item }">
            <span v-if="item.isCreatedByDeleted">{{
              $t("global.concepts.deleted-user")
            }}</span>
            <span v-else>{{ item.createdBy }}</span>
          </template>

          <template #[`item.origin`]="{ item }">
            <router-link
              v-if="item.runId"
              :to="{
                name: 'Run',
                params: { runId: item.runId },
              }"
              class="ooliba-font-color-blue"
            >
              {{ item.origin }}
            </router-link>
          </template>

          <template #[`item.dateCreated`]="{ item }">
            {{ formatDate(item.dateCreated) }}
          </template>

          <template #[`item.open`]="{ item }">
            <v-icon
              v-if="isExcelFile(file?.name) && item.size > 0"
              @click="onOpen(item)"
            >
              mdi-eye-outline
            </v-icon>
          </template>

          <template #[`item.compare`]="{ item }">
            <v-simple-checkbox
              v-if="
                isExcelFile(file?.name) &&
                item.size > 0 &&
                canSelectFileVersion(item)
              "
              v-model="selected[item.id]"
              @input="onCompareSelect($event, item)"
            >
            </v-simple-checkbox>

            <v-tooltip
              bottom
              v-if="
                isExcelFile(file?.name) &&
                item.size > 0 &&
                !canSelectFileVersion(item)
              "
            >
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span> {{ $t("global.concepts.max-files-selected") }}</span>
            </v-tooltip>
          </template>
        </ooliba-basic-table>

        <h6 class="py-4">
          {{ details }}
        </h6>

        <v-tabs class="ooliba-tab" active-class="ooliba-tab-active">
          <v-tab>{{ $t("global.concepts.comment") }}</v-tab>

          <v-tab-item>
            <div>{{ selectedFileVersions[0]?.comment }}</div>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showUpload" width="1000">
      <upload-file
        :fileName="file?.name"
        :fileEnvName="file?.fileEnvName"
        nameDisabled
        envDisabled
        is-edit
        :uploading="uploading"
        @upload="onUpload($event)"
        @cancel="onCancel"
      ></upload-file>
    </v-dialog>
    <v-dialog v-model="showWarnings" width="500">
      <file-upload-warnings
        :uploading="uploading"
        @cancel="onConfirmCancel"
        @confirmUpload="onConfirmUpload"
        :warnings="warnings"
      ></file-upload-warnings>
    </v-dialog>

    <v-dialog v-model="showSpreadSheet" persistent>
      <v-overlay absolute :value="isLoadingSpreadsheet">
        <v-chip color="blue" dark>{{ $t("global.msg.loading") }}</v-chip>
      </v-overlay>

      <v-card>
        <v-icon
          large
          @click="onCloseSpreadsheet"
          color="error"
          style="float: right"
        >
          mdi-alpha-x-box-outline
        </v-icon>

        <v-card-text>
          <iframe
            :src="excelUrl"
            width="100%"
            title="Excel preview"
            class="mt-1"
            ref="excelHtml"
            style="height: 74vh"
            v-if="showSpreadSheet"
            @load="handleExcelLoaded"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </module-template>
</template>

<script>
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import { isExcelFile } from "@/model/util";
import { get, downloadFile, postForm } from "@/model/api";
import { formatDate } from "@/model/util";

import UploadFile from "@/components/UploadFile";
import FileUploadWarnings from "@/components/run/FileUploadWarnings";

import OolibaBasicTable from "@/components/OolibaBasicTable";
import FileEnvChip from "@/components/FileEnvChip";

export default {
  name: "FileVersions",

  components: {
    OolibaBasicTable,
    ModuleTemplate,
    UploadFile,
    FileUploadWarnings,
    FileEnvChip,
  },

  data() {
    return {
      headers: [
        { value: "versionNumber", text: this.$t("global.concepts.version") },
        { value: "dateCreated", text: this.$t("global.concepts.created-on") },
        { value: "createdBy", text: this.$t("global.concepts.created-by") },
        { value: "origin", text: this.$t("global.concepts.origin") },
        { value: "fileSize", text: this.$t("global.concepts.size") },

        {
          text: this.$t("global.action.view"),
          value: "open",
          align: "center",
          sortable: false,
        },

        {
          text: this.$t("global.run.compare"),
          value: "compare",
          align: "center",
          sortable: false,
        },
      ],

      items: [],
      loading: false,
      file: null,
      fileVersions: [],
      selectedFileVersions: [],
      vaultWriteAccess: false,

      showSpreadSheet: false,
      isLoadingSpreadsheet: false,
      excelUrl: "",

      showWarnings: false,
      warnings: [],
      uploadParameters: {},
      showUpload: false,
      uploading: false,

      selected: {},

      channel1: new BroadcastChannel("file-version1-channel"),
      channel2: new BroadcastChannel("file-version2-channel"),

      downloading: false,
    };
  },

  watch: {
    "$store.state.comparingFileVersion1": {
      handler(newVal, oldVal) {
        if (!newVal) {
          this.selected[oldVal?.id] = false;
          return;
        }
        this.selected[newVal.id] = true;
      },
      immediate: true,
    },

    "$store.state.comparingFileVersion2": {
      handler(newVal, oldVal) {
        if (!newVal) {
          this.selected[oldVal?.id] = false;
          return;
        }
        this.selected[newVal.id] = true;
      },
      immediate: true,
    },
  },

  computed: {
    details() {
      return (
        this.$t("global.concepts.details") +
        " version " +
        this.selectedFileVersions[0]?.versionNumber
      );
    },

    canDownload() {
      return this.selectedFileVersions[0]?.size > 0;
    },
  },

  methods: {
    isExcelFile,

    formatDate,

    canSelectFileVersion(item) {
      const canSelect =
        !this.$store.state.comparingFileVersion1 ||
        !this.$store.state.comparingFileVersion2 ||
        this.selected[item.id];
      return canSelect;
    },

    onCompareSelect(event, item) {
      const comparingVersion1 = this.$store.state.comparingFileVersion1;
      const comparingVersion2 = this.$store.state.comparingFileVersion2;
      if (event) {
        const comparingVersion = {
          id: item.id,
          fileId: this.file?.id,
          name: this.file?.name,
          version: item.versionNumber,
          fileEnvIsLocal: this.file?.fileEnvIsLocal,
          fileEnvLabel: this.file?.fileEnvLabel,
          fileEnvColor: this.file?.fileEnvColor,
        };
        if (!comparingVersion1) {
          this.$store.commit("setComparingFileVersion1", comparingVersion);
          this.channel1.postMessage({
            type: "set",
            comparingVersion: comparingVersion,
          });
          return;
        }
        if (!comparingVersion2) {
          this.$store.commit("setComparingFileVersion2", comparingVersion);
          this.channel2.postMessage({
            type: "set",
            comparingVersion: comparingVersion,
          });
        }
        return;
      }
      if (comparingVersion1 && comparingVersion1.id === item.id) {
        this.$store.commit("unsetComparingFileVersion1");
        this.channel1.postMessage({
          type: "unset",
        });
        return;
      }
      if (comparingVersion2 && comparingVersion2.id === item.id) {
        this.$store.commit("unsetComparingFileVersion2");
        this.channel2.postMessage({
          type: "unset",
        });
      }
    },

    handleExcelLoaded() {
      const iframe = this.$refs.excelHtml;
      const pre = iframe.contentDocument.querySelector("body > pre");
      if (pre) {
        const serverError = pre.textContent;
        if (serverError.includes("Max size exceeded")) {
          this.excelUrl = "";
          this.$store.commit("showWarning", this.$t("global.msg.fichierGrand"));
        } else if (serverError.includes("File not found or empty")) {
          this.excelUrl = "";
          this.$store.commit(
            "showWarning",
            this.$t("global.msg.missingEmptyTemplateFile")
          );
        }
      }
      this.isLoadingSpreadsheet = false;
    },

    onCloseSpreadsheet() {
      this.showSpreadSheet = false;
      // Switching tabs in the Excel preview inserts elements into the history stack, so we need to remove these when the preview is closed
      // OOL-2629
      get(
        `/file/workbook-html-sheet-requests/${this.selectedFileVersions[0].id}`
      )
        .then((r) => {
          if (r > 1) {
            window.history.go(-r + 1);
          }
        })
        .catch((err) => this.$store.commit("showError", err));
    },

    onOpen(item) {
      this.isLoadingSpreadsheet = true;
      this.excelUrl = `/compliance-server/api/file/workbook-html/${item.id}/index.html`;
      this.showSpreadSheet = true;
    },

    onRowClick(clickedItem) {
      this.selectedFileVersions = [clickedItem];
    },

    async loadFileVersions() {
      this.loading = true;

      this.fileVersions = await this.getFileVersions(this.$route.params.fileId);

      if (this.fileVersions) {
        this.items = this.setItems(this.fileVersions);
      }
      const version = this.$route.params.version;
      if (version) {
        for (let item of this.items) {
          if (item.versionNumber === parseInt(version)) {
            this.selectedFileVersions = [item];
            break;
          }
        }
      }
      if (this.items && !this.selectedFileVersions?.length) {
        this.selectedFileVersions = [this.items[0]];
      }

      this.loading = false;
    },

    listFiles() {
      this.$router.push({ name: "Files" });
    },

    setItems(files) {
      let items = [];

      files.forEach((value) => {
        let item = {};

        item.id = value.id;
        item.versionNumber = value.versionNumber + 1;
        item.dateCreated = value.dateCreated;
        item.createdBy = value.createdBy;
        item.isCreatedByDeleted = value.isCreatedByDeleted;
        item.size = value.fileSize;
        item.fileSize = `${Math.ceil(value.fileSize / 1000)} KB`;
        item.commentCreatedBy = value.commentCreatedBy;
        item.commentDateCreated = value.commentDateCreated;
        item.comment = value.comment;
        item.origin =
          value.runTitle && value.runId
            ? value.runTitle + " (#" + value.runId + ")"
            : "";
        item.runId = value.runId;

        items.push(item);
      });
      return items;
    },

    onShowUpload() {
      this.showUpload = true;
    },

    async onDownload() {
      this.downloading = true;
      const href = `file/file-version/${this.selectedFileVersions[0].id}/download`;

      await downloadFile(href).catch((err) => {
        this.$store.commit("showError", err);
      });
      this.downloading = false;
    },

    async checkVaultAccess(vaultId) {
      return await get("/vault/" + vaultId + "/write-access").catch((err) =>
        this.$store.commit("showError", err)
      );
    },

    async getFile(fileId) {
      const path = `/file/${fileId}`;

      return await get(path).catch((err) =>
        this.$store.commit("showError", err)
      );
    },

    async getFileVersions(fileId) {
      const path = `/file/${fileId}/versions`;

      return await get(path).catch((err) =>
        this.$store.commit("showError", err)
      );
    },

    onCancel() {
      this.showUpload = false;
    },

    async onUpload(uploadParameters, force) {
      this.uploading = true;
      const formData = new FormData();

      this.uploadParameters.fileName = uploadParameters.fileName;
      this.uploadParameters.environment = uploadParameters.environment;
      this.uploadParameters.file = uploadParameters.file;
      this.uploadParameters.comment = uploadParameters.comment;

      formData.append("fileName", uploadParameters.fileName);
      formData.append("environment", uploadParameters.environment);
      formData.append("file", uploadParameters.file);
      formData.append("force", force);
      formData.append("comment", uploadParameters.comment);

      await postForm("/file/update", formData)
        .then((res) => {
          this.uploading = false;
          this.uploaded(res);
        })
        .catch((error) => {
          this.uploading = false;
          this.$store.commit("showError", error);
        });
    },

    async uploaded(res) {
      if (res.messages?.length > 0) {
        this.showUpload = false;
        this.showWarnings = true;
        this.warnings = res.messages;
      } else {
        this.loadFileVersions().then(() => {
          this.showUpload = false;
          this.showWarnings = false;
        });
      }
    },

    onConfirmCancel() {
      this.showWarnings = false;
    },

    onConfirmUpload() {
      if (this.uploadParameters) {
        this.onUpload(this.uploadParameters, true);
      }
    },
  },

  async mounted() {
    this.loading = true;

    const fileId = this.$route.params.fileId;
    this.file = await this.getFile(fileId);
    if (this.file?.fileEnvIsLocal) {
      const split = this.file.fileEnvName.split("_");
      this.file.bpId = split[1];
    }
    this.vaultWriteAccess = await this.checkVaultAccess(this.file?.vaultId);
    await this.loadFileVersions();

    this.channel1.onmessage = (event) => {
      const { type, comparingVersion } = event.data;

      switch (type) {
        case "set":
          if (comparingVersion) {
            this.$store.commit("setComparingFileVersion1", comparingVersion);
          }
          break;
        case "unset":
          this.$store.commit("unsetComparingFileVersion1");
          break;
      }
    };

    this.channel2.onmessage = (event) => {
      const { type, comparingVersion } = event.data;

      switch (type) {
        case "set":
          if (comparingVersion) {
            this.$store.commit("setComparingFileVersion2", comparingVersion);
          }
          break;
        case "unset":
          this.$store.commit("unsetComparingFileVersion2");
          break;
      }
    };

    this.loading = false;
  },
};
</script>

<style>
.v-input--selection-controls__input {
  margin-right: 0px !important;
}
</style>
